// SegmentSchema.ts
// Defines the schema for persistent event segments (tick batches, candle batches)
// to support one-year replay storage in IndexedDB.

import { TQuote } from 'src/types';

/**
 * Segment types: tick-level data vs aggregated candles.
 */
export type SegmentType = 'tick' | 'candle';

/**
 * Compression methods for segment data.
 */
export type CompressionType = 'none' | 'delta' | 'runlength';

/**
 * Metadata describing a segment's time range, symbol, and size.
 */
export interface SegmentMetadata {
    segmentId: string; // unique ID: `${symbol}-${granularity}-${startEpoch}`
    symbol: string;
    granularity: number; // ms; 0 for pure tick, >0 for candle intervals
    type: SegmentType; // 'tick' or 'candle'
    startEpoch: number; // ms
    endEpoch: number; // ms
    count: number; // number of ticks/candles in segment
    sizeBytes: number; // estimated byte size (for quota tracking)
    compression: CompressionType;
    createdAt: number; // epoch when segment was created
}

/**
 * Tick batch segment: raw tick data with optional compression.
 */
export interface TickSegment {
    metadata: SegmentMetadata;
    ticks: TickData[];
}

/**
 * Compressed tick representation (minimal fields).
 */
export interface TickData {
    epoch: number; // ms
    price: number; // could be delta-encoded
    volume?: number; // optional, could be run-length encoded
    open?: number;  // OHLC fields preserved from candle segments
    high?: number;
    low?: number;
}

/**
 * Candle batch segment: aggregated OHLC data.
 */
export interface CandleSegment {
    metadata: SegmentMetadata;
    candles: CandleData[];
}

/**
 * OHLC candle data.
 */
export interface CandleData {
    epoch: number; // ms (candle start time)
    open: number;
    high: number;
    low: number;
    close: number;
    volume?: number;
}

/**
 * Union type for any segment.
 */
export type Segment = TickSegment | CandleSegment;

/**
 * Segment index entry (lightweight reference stored in memory).
 */
export interface SegmentIndexEntry {
    segmentId: string;
    symbol: string;
    granularity: number;
    type: SegmentType;
    startEpoch: number;
    endEpoch: number;
    count: number;
    sizeBytes: number;
    compression: CompressionType;
    createdAt: number;
    isLoaded: boolean; // true if segment is currently in memory
    lastAccessedAt?: number; // for LRU eviction
}

/**
 * Helper: convert TQuote to TickData.
 * Preserves OHLC data when available (critical for candle replay).
 */
export function quoteToTickData(quote: TQuote): TickData {
    return {
        epoch: quote.DT ? quote.DT.getTime() : Date.parse(`${quote.Date}Z`),
        price: typeof quote.Close === 'number' ? quote.Close : 0,
        volume: typeof quote.Volume === 'number' ? quote.Volume : undefined,
        open: typeof quote.Open === 'number' ? quote.Open : undefined,
        high: typeof quote.High === 'number' ? quote.High : undefined,
        low: typeof quote.Low === 'number' ? quote.Low : undefined,
    };
}

/**
 * Helper: convert TQuote to CandleData.
 */
export function quoteToCandleData(quote: TQuote): CandleData {
    return {
        epoch: quote.DT ? quote.DT.getTime() : Date.parse(`${quote.Date}Z`),
        open: typeof quote.Open === 'number' ? quote.Open : (typeof quote.Close === 'number' ? quote.Close : 0),
        high: typeof quote.High === 'number' ? quote.High : (typeof quote.Close === 'number' ? quote.Close : 0),
        low: typeof quote.Low === 'number' ? quote.Low : (typeof quote.Close === 'number' ? quote.Close : 0),
        close: typeof quote.Close === 'number' ? quote.Close : 0,
        volume: typeof quote.Volume === 'number' ? quote.Volume : undefined,
    };
}

/**
 * Helper: generate segment ID.
 */
export function generateSegmentId(symbol: string, granularity: number, startEpoch: number): string {
    return `${symbol}-${granularity}-${startEpoch}`;
}

/**
 * Helper: estimate segment size in bytes (rough approximation).
 */
export function estimateSegmentSize(count: number, type: SegmentType): number {
    // Rough estimates: tick ~40 bytes, candle ~60 bytes (with overhead)
    const bytesPerItem = type === 'tick' ? 40 : 60;
    return count * bytesPerItem;
}
